// Shared time formatters for the status surface (Pass 5.2).
//
// IST is hardcoded as the absolute-time zone. Rationale: the
// operator + team are in IST; "regional consistency across
// viewers" beats "browser-local rendering," because two
// teammates comparing screenshots / Slack pastes need to be
// looking at the same number. Browser-local rendering would
// silently disagree across machines in different TZs.
//
// Wire format from /system/status is ISO-8601 UTC (e.g.
// "2026-05-18T08:54:00.001Z"). Only the rendering converts;
// the payload is untouched.

const IST_FORMATTER = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Asia/Kolkata",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

/** "5m ago" / "3h ago" / "2d ago" / "—" — timezone-agnostic. */
export function formatRelative(iso: string | null, nowMs: number = Date.now()): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "—";
  const diffSec = Math.round((nowMs - t) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHour = Math.round(diffMin / 60);
  if (diffHour < 48) return `${diffHour}h ago`;
  const diffDay = Math.round(diffHour / 24);
  return `${diffDay}d ago`;
}

/** "2026-05-18 14:24 IST" — 24-hour, IST-suffixed, never browser-local.
 *  Returns empty string on null/invalid input so callers can guard
 *  with truthiness. */
export function formatAbsoluteIst(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  // Intl gives us "DD/MM/YYYY, HH:MM" in en-GB; reshape to the
  // hyphenated YYYY-MM-DD HH:MM that matches the rest of the
  // viewer (logs, terminal pastes, mongosh output).
  const parts = IST_FORMATTER.formatToParts(d).reduce<Record<string, string>>(
    (acc, p) => {
      if (p.type !== "literal") acc[p.type] = p.value;
      return acc;
    },
    {},
  );
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute} IST`;
}

/** Compact "733 msgs · 69 atts" used in the /status list rows. */
export function formatCounts(messageCount: number, attachmentCount: number): string {
  const m = messageCount.toLocaleString();
  const a = attachmentCount.toLocaleString();
  return `${m} msg${messageCount === 1 ? "" : "s"} · ${a} att${attachmentCount === 1 ? "" : "s"}`;
}

export function formatBytes(n: number | null): string {
  if (n === null || n === undefined) return "—";
  if (n < 1024) return `${n} B`;
  const kb = n / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(2)} GB`;
}
